import { 
  Clock, 
  Settings, 
  LogOut, 
  Heart, 
  Pill, 
  FileDown, 
  Image, 
  Link2, 
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  X,
  Users,
  CalendarDays,  // Per Gestione Visite
  Shield,  // Per Scudo Emergenza
  Lock
} from 'lucide-react';

// Logo Component
const Logo = ({ className = "h-14" }) => (
  <img 
    src="/logo-curacheck.png" 
    alt="CuraCheck" 
    className={`${className} object-contain`}
  />
);

// Subscription Status Badge Component
const SubscriptionBadge = ({ subscriptionStatus }) => {
  if (!subscriptionStatus || !subscriptionStatus.display_badge) return null;
  
  const { display_badge } = subscriptionStatus;
  
  const colorClasses = {
    success: 'bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]',
    warning: 'bg-[#FFF3E0] text-[#E65100] border-[#FFCC80]',
    danger: 'bg-[#FFEBEE] text-[#C62828] border-[#EF9A9A]',
    muted: 'bg-[#F5F5F5] text-[#616161] border-[#E0E0E0]'
  };
  
  const iconMap = {
    check: <CheckCircle2 className="w-3.5 h-3.5" />,
    clock: <Clock className="w-3.5 h-3.5" />,
    alert: <AlertTriangle className="w-3.5 h-3.5" />,
    x: <X className="w-3.5 h-3.5" />,
    user: <Users className="w-3.5 h-3.5" />
  };
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${colorClasses[display_badge.color] || colorClasses.muted}`}>
      {display_badge.icon && iconMap[display_badge.icon]}
      {display_badge.text}
    </div>
  );
};

const Sidebar = ({ activeTab, setActiveTab, onLogout, famiglia, mobileOpen, setMobileOpen, subscriptionStatus }) => {
  // Check if user has Scudo access (Standard and above)
  const userPlan = subscriptionStatus?.plan?.toLowerCase() || 'trial';
  const hasScudoAccess = ['standard', 'trial', 'premium', 'fondatori', 'founder'].includes(userPlan);

  const tabs = [
    { id: 'overview', icon: Sparkles, label: 'Panoramica' },
    { id: 'timeline', icon: Clock, label: 'Timeline' },
    { id: 'photos', icon: Image, label: 'Galleria Foto' },
    { id: 'mood', icon: Heart, label: 'Umore' },
    { id: 'farmaci', icon: Pill, label: 'Gestione Routine' },
    { id: 'visite', icon: CalendarDays, label: 'Visite e Specialisti' },
    { id: 'scudo', icon: Shield, label: 'Scudo Emergenza', highlight: true, locked: !hasScudoAccess },
    { id: 'report', icon: FileDown, label: 'Scarica Report' },
    { id: 'link', icon: Link2, label: 'Link Assistente' },
    { id: 'impostazioni', icon: Settings, label: 'Impostazioni' },
  ];

  return (
    <>
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white border-r border-[#E5E7EB] transform transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[#E5E7EB]">
            <Logo className="h-16" />
          </div>

          {famiglia && (
            <div className="p-4 mx-4 mt-4 bg-gradient-to-br from-[#E8F5E9] to-[#F0F9F1] rounded-xl border border-[#C8E6C9]">
              <p className="text-sm text-[#64748B]">Stai monitorando</p>
              <p className="text-lg font-bold text-[#2C3E50]">{famiglia.nome_assistito}</p>
              <p className="text-sm text-[#4A7C59]">Assistente: {famiglia.nome_assistente}</p>
              
              <div className="mt-3">
                <SubscriptionBadge subscriptionStatus={subscriptionStatus} />
              </div>
            </div>
          )}

          <nav className="flex-1 p-4 space-y-1.5 overflow-y-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all ${
                  activeTab === tab.id
                    ? tab.highlight 
                      ? 'bg-red-500 text-white shadow-lg shadow-red-500/20'
                      : 'bg-[#4A7C59] text-white shadow-lg shadow-[#4A7C59]/20'
                    : tab.highlight
                      ? 'text-red-500 hover:bg-red-50 border border-red-200'
                      : 'text-[#64748B] hover:bg-[#F0F4F1]'
                }`}
                data-testid={`nav-${tab.id}`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="flex-1 text-left">{tab.label}</span>
                {tab.locked && (
                  <Lock className="w-4 h-4 opacity-70" aria-label="Non incluso nel tuo piano" data-testid={`nav-${tab.id}-locked`} />
                )}
                {tab.highlight && activeTab !== tab.id && !tab.locked && (
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                )}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-[#E5E7EB]">
            <button
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 font-medium transition-all"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Esci
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
